import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import {
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { EmptyState } from '@/shared/ui';

import { resolveLocalizedText } from '../../lib/localizedText';
import type { ToastSeverity } from '../../lib/useToast';
import type { AttributeOption, CatalogLanguage } from '../../model/types';
import { AttributeOptionFormDialog } from './AttributeOptionFormDialog';

interface AttributeOptionsManagerProps {
  attributeCode: string;
  options: AttributeOption[];
  languages: CatalogLanguage[];
  canManage: boolean;
  onNotify: (severity: ToastSeverity, message: string) => void;
}

/**
 * SELECT/MULTISELECT options manager (user-story-catalog-004, AC3) — add / edit / deactivate,
 * sort order. Only meaningful once the attribute already exists (options are a sub-resource of a
 * saved attribute code), so this is only rendered from the edit form.
 */
export function AttributeOptionsManager({
  attributeCode,
  options,
  languages,
  canManage,
  onNotify,
}: AttributeOptionsManagerProps) {
  const { t, i18n } = useTranslation('catalog');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AttributeOption | null>(null);

  const sorted = [...options].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  // Content-default locale (adr-tenant-005) — preferred over the portal's own UI language when
  // resolving a translated label (see `resolveLocalizedText`'s resolution-order doc).
  const defaultLocale = languages.find((l) => l.defaultLocale)?.locale;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2">{t('attributeOptionsManager.title')}</Typography>
        {canManage ? (
          <Button
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            {t('attributeOptionsManager.addOption')}
          </Button>
        ) : null}
      </Stack>

      {sorted.length === 0 ? (
        <EmptyState
          title={t('attributeOptionsManager.empty.title')}
          description={t('attributeOptionsManager.empty.description')}
        />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('attributeOptionsManager.table.code')}</TableCell>
              <TableCell>{t('attributeOptionsManager.table.label')}</TableCell>
              <TableCell align="right">{t('attributeOptionsManager.table.sortOrder')}</TableCell>
              <TableCell>{t('attributeOptionsManager.table.status')}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((option) => (
              <TableRow key={option.code} hover>
                <TableCell>
                  <Box component="code" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}>
                    {option.code}
                  </Box>
                </TableCell>
                <TableCell>
                  {resolveLocalizedText(option.translations, 'label', i18n.language, option.code, defaultLocale)}
                </TableCell>
                <TableCell align="right">{option.sortOrder ?? 0}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={option.active ? 'success' : 'default'}
                    label={
                      option.active
                        ? t('attributeOptionsManager.activeChip')
                        : t('attributeOptionsManager.inactiveChip')
                    }
                  />
                </TableCell>
                <TableCell align="right">
                  {canManage ? (
                    <Button
                      size="small"
                      startIcon={<EditRoundedIcon fontSize="small" />}
                      onClick={() => {
                        setEditing(option);
                        setDialogOpen(true);
                      }}
                    >
                      {t('common:action.edit')}
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AttributeOptionFormDialog
        open={dialogOpen}
        attributeCode={attributeCode}
        editing={editing}
        languages={languages}
        onClose={() => setDialogOpen(false)}
        onResult={onNotify}
      />
    </Box>
  );
}
